import {
  ListingResource,
  useListingActions,
  useListingField,
  useListingStatus,
  useListingStore,
  useMediaResource,
} from '@brightmls/nextgen.lae.flc';
import { useSidePanel } from '../SidePanelContext';
import { ApplicationEvent, useAnalytics } from '../AnalyticsContext';
import { useFeatureFlags } from '../FeatureFlagsContext';
import { FeatureFlags } from '../../flags';
import { useManageListingsNavigation } from '../../hooks/useManageListingsNavigation';
import { useSellingAgentEmail } from './useSellingAgentEmail';
import { PropertyField } from '../../system/field';
import PublishConfirmation from '../../components/common/PublishConfirmation';
import PublishError from '../../components/common/PublishError';
import ListingSummary from '../../components/common/ListingSummary';
import { navigateTo } from '../../utils';
import { useDebugPreferences } from '../../hooks/useDebugPreferences';
import { useDebugInfoOptional } from '../DebugInfoContext';
import { decompressDebugData } from '../../utils/compression';

const isServiceError = (error: any): boolean => {
  return error && typeof error === 'object' && 'details' in error;
};

export const usePublish = () => {
  const { publishListing } = useListingActions();
  const { showPanel, hidePanel } = useSidePanel();
  const { captureEvent } = useAnalytics();
  const { hasFeature } = useFeatureFlags();
  const { back } = useManageListingsNavigation();
  const { debugPublish } = useDebugPreferences();
  const debugInfo = useDebugInfoOptional();

  const isPublishing = useListingStatus<boolean>('isPublishing', false);

  // Listing field values
  const listingKey = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );
  const listingId = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_ID,
    '',
  );
  const mlsStatus = useListingField(
    ListingResource.Property,
    PropertyField.MLS_STATUS,
    '',
  );
  const isListingOE = useListingField(
    ListingResource.Property,
    PropertyField.OFFICE_EXCLUSIVE_YN,
    false,
  );
  const propertyType = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );
  const listPrice = useListingField(
    ListingResource.Property,
    PropertyField.LIST_PRICE,
    0,
  );
  const address = useListingField(
    ListingResource.Property,
    PropertyField.UNPARSED_ADDRESS,
    '',
  );
  const sellingAgentMlsId = useListingField(
    ListingResource.Property,
    PropertyField.BUYER_AGENT_MLS_ID,
    '',
  );
  const coSellingAgentMlsId = useListingField(
    ListingResource.Property,
    PropertyField.CO_BUYER_AGENT_MLS_ID,
    '',
  );
  const { value: photoOption, updateValue: updatePhotoOption } =
    useListingField(ListingResource.Property, PropertyField.PHOTO_OPTION, '');

  // Media for photo validation
  const { count: mediaCount, media } = useMediaResource();

  // Feature flag for photo option check
  const shouldCheckPhotoOption = hasFeature(
    FeatureFlags.NGW_CHECK_PHOTO_OPTION_VS_PHOTO_COUNT,
  );

  // Selling agent email hook
  const { maybeSendSellingAgentEmail } = useSellingAgentEmail({
    listingKey: listingKey.value,
    listingId: listingId.value,
    mlsStatus: mlsStatus.value,
    isListingOE: isListingOE.value,
    sellingAgentMlsId: sellingAgentMlsId.value,
    coSellingAgentMlsId: coSellingAgentMlsId.value,
  });

  const finalize = () => {
    // Navigate back to the listing page (or the home page designated from the tenant)
    back();

    // Inform autosave and other operations that publish is no longer in progress
    isPublishing.updateValue(false);

    // Let FLC know that the listing context is unlocked
    useListingStore.setState({
      published: false,
    });

    // Close the panel
    hidePanel();
  };

  const publish = async () => {
    // Inform autosave and other operations that publish is in progress
    isPublishing.updateValue(true);

    const submit = async (loading: (isLoading: boolean) => void) => {
      loading(true);

      try {
        // Handle mismatched Photo Option and photo media count
        // Only run in NGW for BrightMLS
        if (shouldCheckPhotoOption) {
          const photos = media.filter((m) => m.MEDIA_CATEGORY === 'Photo');
          if (photoOption === 'No Photo per Seller' && photos.length > 0) {
            updatePhotoOption('Lister Will Upload All');
          }
        }

        // Publish & let's capture the listing id if the publish routine provides one
        const { ListingId: listingId, _debug: rawPublishDebugInfo } =
          await publishListing();

        // Decompress debug info if present
        const publishDebugInfo = rawPublishDebugInfo
          ? decompressDebugData(rawPublishDebugInfo)
          : undefined;

        // Store debug info from the successful response
        if (publishDebugInfo && debugInfo) {
          debugInfo.setLastPublishDebug(publishDebugInfo);
        }

        // Create a helper function for field access in the confirmation component
        const getResourceFieldValue = (
          resourceType: ListingResource,
          fieldName: string,
          fallbackValue: any,
        ) => {
          const state = useListingStore.getState();
          const value = state.getResourceFieldValue(
            resourceType,
            fieldName,
            fallbackValue,
          );

          return value;
        };

        showPanel(
          'Published Successfully',
          <PublishConfirmation
            getResourceValue={getResourceFieldValue}
            onListingsClick={finalize}
            listingId={listingId}
            debugInfo={debugPublish ? publishDebugInfo : undefined}
          />,
          null,
          375,
          () => finalize(),
        );

        // Capture analytics event using the field values we already have
        captureEvent(ApplicationEvent.ListingPublish, {
          listingKey: listingKey.value,
          listingId: listingId.value,
          propertyType: propertyType.value,
          listPrice: listPrice.value,
          address: address.value,
          mediaCount: mediaCount,
        });

        await maybeSendSellingAgentEmail();
      } catch (errorDetails: any) {
        // On failure, reset the publishing state
        isPublishing.updateValue(false);
        const error: any = isServiceError(errorDetails)
          ? errorDetails
          : {
              error: 'Error publishing listing',
              message: errorDetails.message || errorDetails.error,
              details: errorDetails.details,
            };

        // Extract and decompress debug info from error if available
        const errorDebugInfo = errorDetails?._debug
          ? decompressDebugData(errorDetails._debug)
          : undefined;

        // Store debug info from the error response
        if (errorDebugInfo && debugInfo) {
          debugInfo.setLastPublishDebug(errorDebugInfo);
        }

        if (errorDetails.message?.includes('PUBLISH_IN_PROGRESS')) {
          error.message = errorDetails.message.split(':')[1];
          // Set published to true to turn off autosave
          useListingStore.setState({
            published: true,
          });
          showPanel(
            'Publish Error',
            <PublishError
              error={error}
              onEditListing={() => navigateTo(window.location.href)}
              debugInfo={debugPublish ? errorDebugInfo : undefined}
            />,
            null,
            375,
            () => navigateTo(window.location.href),
          );
        } else {
          showPanel(
            'Publish Error',
            <PublishError
              error={error}
              onEditListing={hidePanel}
              debugInfo={debugPublish ? errorDebugInfo : undefined}
            />,
          );
        }
      } finally {
        loading(false);
      }
    };

    // Helper to get field value for the ListingSummary component
    const getFieldValue = (fieldName: PropertyField, fallbackValue: any) => {
      const state = useListingStore.getState();
      return state.getResourceFieldValue(
        ListingResource.Property,
        fieldName,
        fallbackValue,
      );
    };

    showPanel(
      'Publish Confirmation',
      <ListingSummary onSubmit={submit} getFieldValue={getFieldValue} />,
      undefined,
      undefined,
      () => {
        console.error('Publish Confirmation Cancelled');
        isPublishing.updateValue(false);
      },
    );
  };

  return { publish };
};
